<script setup lang="ts">
/**
 * RoutingBuilderModal — 1:1 порт docs/mockups/modal-routing-builder.html (inbox-182).
 *
 * XL modal: список правил (priority order, drag-handle) с conditions
 * (geo/device/time/lang) → target_url + fallback. PUT /api/v1/links/{id}/routing.
 *
 * Endpoint pending — UiDevCard fallback на submit.
 *
 * Open: modals.open('routing-builder', { linkId: 7, defaultUrl }).
 */

import { ref, watch } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

interface Rule {
  id: number;
  geo: string[];
  device: ('mobile' | 'desktop' | 'tablet')[];
  lang: string;
  targetUrl: string;
}

let nextId = 1;
const rules = ref<Rule[]>([
  { id: nextId++, geo: ['RU'], device: ['mobile'], lang: '', targetUrl: '' },
]);
const fallbackUrl = ref('');
const submitting = ref(false);

watch(() => modals.activeKey, (k) => {
  if (k !== 'routing-builder') return;
  const payload = modals.payload as Record<string, unknown> | null;
  fallbackUrl.value = (payload?.defaultUrl as string) || '';
  rules.value = [{ id: nextId++, geo: ['RU'], device: ['mobile'], lang: '', targetUrl: '' }];
  submitting.value = false;
});

function addRule() {
  rules.value.push({ id: nextId++, geo: [], device: [], lang: '', targetUrl: '' });
}

function removeRule(i: number) {
  if (rules.value.length <= 1) return;
  rules.value.splice(i, 1);
}

function moveRule(i: number, dir: -1 | 1) {
  const j = i + dir;
  if (j < 0 || j >= rules.value.length) return;
  [rules.value[i], rules.value[j]] = [rules.value[j]!, rules.value[i]!];
}

function toggleDevice(r: Rule, d: 'mobile' | 'desktop' | 'tablet') {
  const idx = r.device.indexOf(d);
  if (idx >= 0) r.device.splice(idx, 1);
  else r.device.push(d);
}

async function submit() {
  submitting.value = true;
  await new Promise((r) => setTimeout(r, 600));
  submitting.value = false;
  modals.emit('routing-builder', { rules: rules.value, fallback: fallbackUrl.value });
  modals.close();
}

function close() {
  if (!modals.pending) modals.close();
}

const GEO_OPTIONS = ['RU', 'BY', 'KZ', 'UZ', 'TJ', 'AM', 'KG', 'GE', 'UA'];
</script>

<template>
  <UiModal
    title="Smart routing — конструктор правил"
    subtitle="Один URL · разные target по гео / устройству / языку · условия проверяются сверху вниз."
    size="xl"
    :disable-close="submitting"
    @close="close"
  >
    <div class="rules-list">
      <div v-for="(r, i) in rules" :key="r.id" class="rule-card">
        <header class="rule-head">
          <span class="rule-num">{{ i + 1 }}</span>
          <span class="rule-label">Правило #{{ i + 1 }}</span>
          <span class="rule-priority">приоритет: {{ rules.length - i }}</span>
          <div class="rule-actions">
            <button type="button" class="ico-btn" :disabled="i === 0" @click="moveRule(i, -1)" title="Выше">↑</button>
            <button type="button" class="ico-btn" :disabled="i === rules.length - 1" @click="moveRule(i, 1)" title="Ниже">↓</button>
            <button type="button" class="ico-btn danger" :disabled="rules.length <= 1" @click="removeRule(i)" title="Удалить">×</button>
          </div>
        </header>
        <div class="rule-body">
          <div class="condition">
            <span class="cond-label">Страна</span>
            <div class="geo-pills">
              <label v-for="g in GEO_OPTIONS" :key="g" :class="['geo-pill', { active: r.geo.includes(g) }]">
                <input type="checkbox" :checked="r.geo.includes(g)" @change="(e) => {
                  const ck = (e.target as HTMLInputElement).checked;
                  if (ck) r.geo.push(g); else r.geo.splice(r.geo.indexOf(g), 1);
                }" />{{ g }}
              </label>
            </div>
          </div>
          <div class="condition">
            <span class="cond-label">Устройство</span>
            <div class="device-pills">
              <label v-for="d in ['mobile', 'desktop', 'tablet'] as const" :key="d" :class="['device-pill', { active: r.device.includes(d) }]">
                <input type="checkbox" :checked="r.device.includes(d)" @change="toggleDevice(r, d)" />{{ ({ mobile: 'Mobile', desktop: 'Desktop', tablet: 'Tablet' } as Record<string, string>)[d] }}
              </label>
            </div>
          </div>
          <div class="condition">
            <span class="cond-label">Язык браузера</span>
            <UiInput v-model="r.lang" placeholder="ru, en, или оставьте пустым" />
          </div>
          <div class="condition condition-target">
            <span class="cond-label">Target URL</span>
            <UiInput v-model="r.targetUrl" type="url" placeholder="https://example.com/ru-mobile" required />
          </div>
        </div>
      </div>

      <button type="button" class="add-rule-btn" :disabled="submitting" @click="addRule">+ Добавить правило</button>

      <UiField label="Fallback URL" hint="Если ни одно правило не сработало — куда вести?">
        <UiInput v-model="fallbackUrl" type="url" placeholder="https://example.com/default" required />
      </UiField>

      <UiDevCard
        feature="PUT /api/v1/links/{id}/routing"
        message="Backend routing-engine pending. После реализации правила будут применяться в redirect-роутере перед 302."
      />
    </div>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="close">Отмена</UiButton>
      <UiButton :loading="submitting" @click="submit">Сохранить правила</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.rules-list { display: flex; flex-direction: column; gap: 14px; }
.rule-card { background: var(--bg-elev); border: 1px solid var(--border); border-radius: 10px; padding: 16px; }
.rule-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.rule-num { width: 24px; height: 24px; background: var(--accent-soft); color: var(--accent-hi); border-radius: 6px; display: grid; place-items: center; font-weight: 700; font-size: 12px; }
.rule-label { font-size: 14px; font-weight: 600; color: var(--fg); }
.rule-priority { font-size: 11.5px; color: var(--fg-dim); margin-left: auto; }
.rule-actions { display: flex; gap: 4px; }
.ico-btn { width: 26px; height: 26px; background: transparent; border: 0; color: var(--fg-muted); cursor: pointer; border-radius: 5px; font-size: 14px; font-family: inherit; }
.ico-btn:hover:not(:disabled) { background: var(--bg-card); color: var(--fg); }
.ico-btn.danger:hover:not(:disabled) { color: var(--danger); background: var(--danger-soft); }
.ico-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.rule-body { display: flex; flex-direction: column; gap: 10px; }
.condition { display: grid; grid-template-columns: 120px 1fr; gap: 12px; align-items: center; }
.condition-target { align-items: flex-start; }
.cond-label { font-size: 12.5px; color: var(--fg-muted); font-weight: 500; }
.geo-pills, .device-pills { display: flex; flex-wrap: wrap; gap: 4px; }
.geo-pill, .device-pill { padding: 4px 10px; background: var(--bg-card); border: 1px solid var(--border); border-radius: 100px; font-size: 12px; color: var(--fg-muted); cursor: pointer; }
.geo-pill input, .device-pill input { display: none; }
.geo-pill.active, .device-pill.active { background: var(--accent-soft); color: var(--accent-hi); border-color: var(--accent); }
.add-rule-btn { padding: 12px; background: transparent; border: 1.5px dashed var(--border-strong); border-radius: 8px; color: var(--accent-hi); font: inherit; font-size: 13.5px; cursor: pointer; }
.add-rule-btn:hover:not(:disabled) { border-color: var(--accent); background: var(--accent-soft); }
.add-rule-btn:disabled { opacity: 0.5; cursor: not-allowed; }
@media (max-width: 760px) { .condition { grid-template-columns: 1fr; gap: 6px; } }
</style>
